import React from 'react';
import { Droplets, Truck, HardHat, Monitor, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent } from './ui/card';
import { services } from '../data/mock';

const iconMap = {
  Droplets,
  Truck,
  HardHat,
  Monitor
};

export const Services = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#01A9DB]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F58020]/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Nuestros Servicios
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#01A9DB] to-[#F58020] mx-auto rounded-full mb-6"></div>
          <p className="text-lg text-slate-600">
            Soluciones integrales para empresas públicas, privadas y personas naturales en la región
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            
            return (
              <Card
                key={service.id}
                className="group relative overflow-hidden border-2 border-slate-100 hover:border-cyan-200 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 bg-white"
              >
                {/* Image Container */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-6 left-6 w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#01A9DB] to-[#F58020] rounded-lg flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Service Number */}
                  <div className="absolute top-6 right-6 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-lg font-bold text-[#F58020]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Title Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <CardContent className="p-6">
                  <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-start space-x-3 group/item"
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center mt-0.5 group-hover/item:bg-[#01A9DB] transition-colors duration-300">
                          <CheckCircle2 className="w-4 h-4 text-[#01A9DB] group-hover/item:text-white transition-colors duration-300" />
                        </div>
                        <span className="text-slate-700 font-medium group-hover/item:text-slate-900 transition-colors">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={scrollToContact}
                    className="w-full group/btn bg-gradient-to-r from-[#01A9DB] to-[#F58020] hover:from-[#0891b2] hover:to-[#ea580c] text-white shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Solicitar Cotización
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>

                {/* Decorative Corner Element */}
                <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-gradient-to-br from-[#01A9DB]/10 to-[#F58020]/10 rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="bg-gradient-to-r from-[#01A9DB] to-[#F58020] rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Necesitas un servicio personalizado?
            </h3>
            <p className="text-cyan-50 text-lg mb-8 max-w-2xl mx-auto">
              Contáctanos para discutir tus necesidades específicas. Ofrecemos soluciones a medida para cada proyecto.
            </p>
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-white text-[#01A9DB] hover:bg-slate-50 hover:text-[#F58020] text-lg px-8 py-6 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 font-bold"
            >
              Contactar Ahora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
